/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/reviewNotifications.scss';

import React, {MouseEventHandler, useEffect, useMemo, useRef, useState} from 'react';
import {NotificationType} from "../../../util/enums";
import {Rating} from "../../../util/types";
import Notification from "../../../models/notificationModel";
import {AwesomeIcon, Checkbox, CloseButton, hasHover, voidFunction} from '@dvrd/dvr-controls';
import {renderPlural} from "../../../util/utils";
import BolRating from "../../bolRating/bolRating";
import classNames from 'classnames';
import {NotificationProps, NotificationTypeProps} from "../notificationsPage";
import useDefaultNotification from "../useDefaultNotification";
import NotificationEmail from "../notificationComponents/notificationEmail";
import NotificationProducts from "../notificationComponents/notificationProducts";
import NotificationActions from "../notificationComponents/notificationActions";
import NotificationIntervalComp from "../notificationComponents/notificationInterval";
import PremiumNotificationLabel from "../premiumNotificationLabel";

function ReviewNotification(props: NotificationProps) {
    const {
        notification, onChangeNotification, loading, onDeleteNotification, onSendTestNotification, disabled, canDelete,
        canEdit, onReplaceNotification
    } = props;
    const [ratingsOpen, setRatingsOpen] = useState(false);
    const ratingsRef = useRef<HTMLDivElement>(null);
    const [onChangeEmailAddresses] = useDefaultNotification(props);

    function onSelectRating(rating: Rating) {
        return function() {
            const reviewSettings = notification.reviewConfig;
            if (reviewSettings.ratings.has(rating)) reviewSettings.ratings.delete(rating);
            else reviewSettings.ratings.add(rating);
            notification.flex = reviewSettings.dump();
            onChangeNotification(notification);
        }
    }

    function onDocumentClick() {
        if (!hasHover(ratingsRef.current)) setRatingsOpen(false);
    }

    function onOpenRatings() {
        setRatingsOpen(!ratingsOpen);
    }

    function onCloseRatings() {
        setRatingsOpen(false);
    }

    function renderRatings() {
        let label: string;
        const selected = notification.reviewConfig.ratings;
        if (selected.size === 5 || !selected.size) label = 'Alle scores';
        else if (selected.size === 1) {
            const rating = Array.from(selected)[0];
            label = `${rating} ${renderPlural(rating, 'ster', 'sterren')}`;
        } else label = `${selected.size} scores`;
        return (
            <section className='ratings-container' ref={ratingsRef}>
                <label className='fixed-text'>Bij nieuwe reviews met</label>
                {renderButton(label, onOpenRatings, 'score-button')}
                <div className={classNames('ratings-select-container', (ratingsOpen && !disabled) && 'open')}>
                    <CloseButton onClick={onCloseRatings} className='close-ratings'/>
                    {renderRating(1)}
                    {renderRating(2)}
                    {renderRating(3)}
                    {renderRating(4)}
                    {renderRating(5)}
                </div>
            </section>
        )
    }

    function renderRating(rating: Rating) {
        const selected = notification.reviewConfig.ratings.has(rating);
        return (
            <div className='rating-container' onClick={onSelectRating(rating)}>
                <Checkbox checked={selected} onCheck={voidFunction}/>
                <BolRating value={rating} staticStars/>
            </div>
        )
    }

    function renderButton(label: string, onClick: MouseEventHandler, className?: string) {
        const _disabled = loading || disabled || !canEdit;
        return (
            <div className={classNames('var-button', _disabled && 'disabled', className)}
                 onClick={_disabled ? undefined : onClick}>
                <label className='button-label'>{label}</label>
            </div>
        )
    }

    useEffect(() => {
        if (ratingsOpen)
            window.setTimeout(() => {
                document.addEventListener('click', onDocumentClick)
            });
        else document.removeEventListener('click', onDocumentClick);

        return function() {
            document.removeEventListener('click', onDocumentClick);
        }
    }, [ratingsOpen]);

    return (
        <div className={classNames('review-notification notification-config', disabled && 'disabled')}>
            <label className='notification-title'>
                <AwesomeIcon name='star' className='type-icon'/> Review notificatie <PremiumNotificationLabel/>
            </label>
            <div className='notification'>
                {renderRatings()}
                <NotificationProducts onSuccess={onReplaceNotification} notification={notification}
                                      disabled={loading || disabled || !canEdit}/>
                <NotificationEmail onChangeEmailAddresses={onChangeEmailAddresses}
                                   emailAddresses={notification.emailAddresses}
                                   disabled={loading || disabled || !canEdit}/>
                <NotificationIntervalComp onChangeNotification={onChangeNotification}
                                          disabled={loading || disabled || !canEdit} notification={notification}/>
            </div>
            <NotificationActions onDeleteNotification={onDeleteNotification}
                                 onSendTestNotification={onSendTestNotification} disabled={disabled}
                                 notification={notification} canDelete={canDelete}/>
        </div>
    )
}

export default function ReviewNotifications(props: NotificationTypeProps) {
    const {notifications, moduleActive} = props;
    const reviewNotifications = useMemo(() => {
        return notifications.filter(
            (notification: Notification) => notification.notificationType === NotificationType.REVIEWS);
    }, [notifications]);
    if (!reviewNotifications.length) return null;


    function renderNotification(notification: Notification, index: number) {
        return <ReviewNotification key={index} notification={notification} disabled={!moduleActive} {...props}/>;
    }

    return (
        <div className='notification-type review-notifications'>
            <div className='notifications'>
                {reviewNotifications.map(renderNotification)}
            </div>
        </div>
    )
}